<c-container fluid class="order-detail-page py-4">
  <!-- Loading State -->
  @if (loading()) {
    <div class="text-center py-5">
      <c-spinner color="primary" variant="grow"></c-spinner>
      <p class="mt-3 text-body-secondary">{{ 'my_orders_loading' | translate }}</p>
    </div>
  }

  <!-- Error State -->
  @if (error() && !loading()) {
    <c-alert color="danger" class="mb-4">
      {{ error() }}
    </c-alert>
  }

  <!-- Order Content -->
  @if (!loading() && !error() && order(); as ord) {
    <!-- Back Button -->
    <c-row class="mb-4">
      <c-col xs="12">
        <button
          cButton
          color="link"
          class="p-0 d-flex align-items-center text-decoration-none"
          (click)="goBack()"
        >
          <svg cIcon name="cilArrowLeft" size="lg" class="me-2"></svg>
          <span class="fs-6">{{ 'my_orders_back_to_orders' | translate }}</span>
        </button>
      </c-col>
    </c-row>

    <!-- Order Header -->
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card class="shadow-sm border-0">
          <c-card-header class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ 'my_orders_order' | translate }} #{{ ord.id }}
            </h5>
            <c-badge [color]="statusBadgeColor()">
              {{ formattedStatus() }}
            </c-badge>
          </c-card-header>

          <c-card-body>
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="fw-bold" style="width: 150px;">{{ 'my_orders_order_date' | translate }}:</td>
                  <td>{{ formatDateTime(ord.created_at) }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">{{ 'my_orders_payment_method' | translate }}:</td>
                  <td>{{ formatPaymentMethod(ord.payment_method) }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">{{ 'my_orders_total_price' | translate }}:</td>
                  <td>{{ formatCurrency(ord.total_price) }}</td>
                </tr>
                @if (hasPaymentCosts()) {
                  <tr>
                    <td class="fw-bold">{{ 'my_orders_payment_costs' | translate }}:</td>
                    <td>{{ formatCurrency(ord.payment_costs) }}</td>
                  </tr>
                  <tr>
                    <td class="fw-bold fs-5">{{ 'my_orders_grand_total' | translate }}:</td>
                    <td class="fs-5 fw-bold text-primary">{{ formatCurrency(grandTotal()) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- Web3 Payment Details -->
    @if (ord.payment_method === 'web3_payment' && ord.web3_transaction_hash) {
      <c-row class="mb-4">
        <c-col xs="12">
          <c-card class="shadow-sm border-0">
            <c-card-header>
              <strong>{{ 'my_orders_web3_payment_details' | translate }}</strong>
            </c-card-header>
            <c-card-body>
              <table class="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <td class="fw-bold" style="width: 150px;">{{ 'my_orders_transaction_hash' | translate }}:</td>
                    <td class="font-monospace small">
                      <div class="d-flex flex-column gap-2">
                        <span class="text-break">{{ ord.web3_transaction_hash }}</span>
                        <div class="d-flex gap-2">
                          <button
                            cButton
                            color="secondary"
                            size="sm"
                            (click)="copyToClipboard(ord.web3_transaction_hash!)"
                          >
                            <svg cIcon name="cilCopy" size="sm"></svg>
                            <span class="ms-1">{{ 'my_orders_copy' | translate }}</span>
                          </button>
                          <a
                            [href]="'https://bscscan.com/tx/' + ord.web3_transaction_hash"
                            target="_blank"
                            rel="noopener noreferrer"
                            cButton
                            color="primary"
                            size="sm"
                          >
                            <svg cIcon name="cilExternalLink" size="sm"></svg>
                            <span class="ms-1">{{ 'my_orders_view_bscscan' | translate }}</span>
                          </a>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="fw-bold">{{ 'my_orders_confirmations' | translate }}:</td>
                    <td>
                      <c-badge [color]="(ord.web3_confirmations ?? 0) >= 12 ? 'success' : 'warning'">
                        {{ ord.web3_confirmations ?? 0 }} / 12
                      </c-badge>
                    </td>
                  </tr>
                  @if (ord.web3_verified_at) {
                    <tr>
                      <td class="fw-bold">{{ 'my_orders_verified_at' | translate }}:</td>
                      <td>{{ formatDateTime(ord.web3_verified_at) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </c-card-body>
          </c-card>
        </c-col>
      </c-row>
    }

    <!-- eWallet / Credit Card Info -->
    @if (ord.payment_method === 'ewallet') {
      <c-row class="mb-4">
        <c-col xs="12">
          <c-card class="shadow-sm border-0">
            <c-card-header>
              <strong>{{ 'my_orders_ewallet_payment_details' | translate }}</strong>
            </c-card-header>
            <c-card-body>
              <p class="mb-0">
                <svg cIcon name="cilInfo" size="sm" class="me-1"></svg>
                {{ 'my_orders_ewallet_message' | translate }}
              </p>
            </c-card-body>
          </c-card>
        </c-col>
      </c-row>
    }

    @if (ord.payment_method === 'credit_card') {
      <c-row class="mb-4">
        <c-col xs="12">
          <c-card class="shadow-sm border-0">
            <c-card-header>
              <strong>{{ 'my_orders_credit_card_payment_details' | translate }}</strong>
            </c-card-header>
            <c-card-body>
              <p class="mb-0">
                <svg cIcon name="cilInfo" size="sm" class="me-1"></svg>
                {{ 'my_orders_stripe_message' | translate }}
              </p>
            </c-card-body>
          </c-card>
        </c-col>
      </c-row>
    }

    <!-- Order Items -->
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card class="shadow-sm border-0">
          <c-card-header>
            <strong>{{ 'my_orders_order_items' | translate }}</strong>
          </c-card-header>
          <c-card-body class="p-0">
            <div class="table-responsive">
              <table class="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>{{ 'my_orders_product' | translate }}</th>
                    <th class="text-center">{{ 'my_orders_quantity' | translate }}</th>
                    <th class="text-end">{{ 'my_orders_unit_price' | translate }}</th>
                    <th class="text-end">{{ 'my_orders_subtotal' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of ord.order_details || []; track item.id) {
                    <tr>
                      <td>{{ item.product_name }}</td>
                      <td class="text-center">{{ item.quantity }}</td>
                      <td class="text-end">{{ formatCurrency(item.price) }}</td>
                      <td class="text-end">{{ formatCurrency(item.subtotal) }}</td>
                    </tr>
                  }
                </tbody>
                <tfoot class="table-secondary">
                  <tr>
                    <td colspan="3" class="text-end fw-bold">{{ 'my_orders_total_price' | translate }}:</td>
                    <td class="text-end fw-bold">{{ formatCurrency(ord.total_price) }}</td>
                  </tr>
                  @if (ord.payment_costs && parseFloat(ord.payment_costs) > 0) {
                    <tr>
                      <td colspan="3" class="text-end fw-bold">{{ 'my_orders_payment_costs' | translate }}:</td>
                      <td class="text-end fw-bold">{{ formatCurrency(ord.payment_costs) }}</td>
                    </tr>
                    <tr>
                      <td colspan="3" class="text-end fw-bold fs-5">{{ 'my_orders_grand_total' | translate }}:</td>
                      <td class="text-end fw-bold fs-5 text-primary">{{ formatCurrency(grandTotal()) }}</td>
                    </tr>
                  }
                </tfoot>
              </table>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- Actions (Download Invoice) -->
    @if (ord.status === 'completed') {
      <c-row class="mb-4">
        <c-col xs="12">
          <c-card class="shadow-sm border-0">
            <c-card-header>
              <strong>{{ 'my_orders_actions' | translate }}</strong>
            </c-card-header>
            <c-card-body>
              <div class="d-flex gap-2">
                <button
                  cButton
                  color="primary"
                  class="flex-grow-1"
                  (click)="downloadBill(ord.id)"
                >
                  <svg cIcon name="cilCloudDownload" size="sm" class="me-2"></svg>
                  {{ 'my_orders_download_bill' | translate }}
                </button>
                <button
                  cButton
                  color="info"
                  variant="outline"
                  class="flex-grow-1"
                  (click)="previewBill(ord.id)"
                >
                  <svg cIcon name="cilFile" size="sm" class="me-2"></svg>
                  {{ 'my_orders_preview_bill' | translate }}
                </button>
              </div>
            </c-card-body>
          </c-card>
        </c-col>
      </c-row>
    }

    <!-- Timeline -->
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card class="shadow-sm border-0">
          <c-card-header>
            <strong>{{ 'my_orders_timeline' | translate }}</strong>
          </c-card-header>
          <c-card-body>
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="fw-bold" style="width: 150px;">{{ 'my_orders_created' | translate }}:</td>
                  <td>{{ formatDateTime(ord.created_at) }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">{{ 'my_orders_last_updated' | translate }}:</td>
                  <td>{{ formatDateTime(ord.updated_at) }}</td>
                </tr>
              </tbody>
            </table>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  }
</c-container>

<!-- Bill Preview Modal -->
<c-modal
  [visible]="billPreviewModalVisible()"
  (visibleChange)="billPreviewModalVisible.set($event)"
  backdrop="static"
  size="xl"
  scrollable="true"
>
  <c-modal-header class="d-flex justify-content-between align-items-center">
    <h5 cModalTitle class="mb-0">{{ 'my_orders_bill_preview' | translate }}</h5>
    <button
      type="button"
      cButtonClose
      [attr.aria-label]="'my_orders_close' | translate"
      (click)="closeBillPreview()"
    >
    </button>
  </c-modal-header>

  <c-modal-body class="p-0">
    @if (billPreviewUrl()) {
      <iframe
        [src]="billPreviewUrl()"
        style="width: 100%; height: 80vh; border: none;"
        [title]="'my_orders_bill_preview' | translate"
      ></iframe>
    }
  </c-modal-body>

  <c-modal-footer>
    <button cButton color="secondary" (click)="closeBillPreview()">
      {{ 'my_orders_close' | translate }}
    </button>
  </c-modal-footer>
</c-modal>